import React, { useState, useEffect } from 'react';
import { Search, Eye, FileText, Calendar, Building, TrendingUp, Package, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import API_BASE_URL from "../config";
import POApprovalModal from '../Modal/POApprovalModal';

const POApproval = () => {
  const [poHeaders, setPoHeaders] = useState([]);
  const [vendors, setVendors] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPO, setSelectedPO] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // ✅ Default sort: latest first (poCreatedDate descending)
  const [sortConfig, setSortConfig] = useState({ key: 'poCreatedDate', direction: 'desc' });
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    fetchPOHeaders();
    fetchVendors();
  }, []);

  const fetchPOHeaders = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/T_PO_Header`);
      const data = await res.json();
      setPoHeaders(data);
    } catch (error) {
      console.error('Error fetching PO headers:', error);
      alert('Error loading PO list');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Vendor`);
      const data = await res.json();
      const vendorMap = {};
      data.forEach(v => { vendorMap[v.id] = v.VendorName; });
      setVendors(vendorMap);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      0: { label: 'Draft', color: 'bg-gray-100 text-gray-800' },
      1: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
      2: { label: 'Approved', color: 'bg-green-100 text-green-800' },
      3: { label: 'Rejected', color: 'bg-red-100 text-red-800' },
      4: { label: 'Cancelled', color: 'bg-gray-100 text-gray-800' }
    };
    const statusInfo = statusMap[status] || { label: 'Unknown', color: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />;
    return sortConfig.direction === 'asc'
      ? <ArrowUp className="w-4 h-4 ml-1" />
      : <ArrowDown className="w-4 h-4 ml-1" />;
  };

  const sortData = (data) => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      if (typeof aValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleViewPO = (po) => {
    setSelectedPO(po);
    setIsModalOpen(true);
  };

  const handleApprove = async (poId, signature, remarks) => {
    try {
      const approvalData = {
        poHeaderId: poId,
        approverUserLevel: 4,
        response: 1,
        responseDateTime: new Date().toISOString(),
        responseRemarks: remarks,
        approverId: 9,
        signature: signature
      };
      const res = await fetch(`${API_BASE_URL}api/T_PO_Approval`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(approvalData)
      });
      if (res.ok) {
        await fetch(`${API_BASE_URL}api/T_PO_Header/${poId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 2 })
        });
        alert('PO Approved Successfully!');
        fetchPOHeaders();
        setIsModalOpen(false);
      }
    } catch (error) {
      console.error('Error approving PO:', error);
      alert('Error approving PO');
    }
  };

  const handleReject = async (poId, signature, remarks) => {
    try {
      const approvalData = {
        poHeaderId: poId,
        approverUserLevel: 4,
        response: 0,
        responseDateTime: new Date().toISOString(),
        responseRemarks: remarks,
        approverId: 9,
        signature: signature
      };
      const res = await fetch(`${API_BASE_URL}api/T_PO_Approval`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(approvalData)
      });
      if (res.ok) {
        await fetch(`${API_BASE_URL}api/T_PO_Header/${poId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 3 })
        });
        alert('PO Rejected');
        fetchPOHeaders();
        setIsModalOpen(false);
      }
    } catch (error) {
      console.error('Error rejecting PO:', error);
      alert('Error rejecting PO');
    }
  };

  const filteredPOs = sortData(poHeaders.filter(po => {
    const vendorName = vendors[po.vendorId]?.toLowerCase() || '';
    const matchesSearch =
      po.poNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      po.shipTo_Location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vendorName.includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || po.status.toString() === statusFilter;
    return matchesSearch && matchesStatus;
  }));

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPOs.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPOs.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      for (let i = 1; i <= 4; i++) pages.push(i);
      pages.push('...'); pages.push(totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1); pages.push('...');
      for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1); pages.push('...');
      pages.push(currentPage - 1); pages.push(currentPage); pages.push(currentPage + 1);
      pages.push('...'); pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="min-h-screen from-blue-50 via-slate-50 to-blue-100 p-6">
      <div className="max-w-8xl mx-auto">

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 shadow-lg mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                <Package className="w-7 h-7" />
                Purchase Order Approval
              </h1>
              <p className="text-blue-100 text-sm mt-1">Review and approve purchase orders</p>
            </div>
            <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
              <p className="text-xs text-white/80 font-semibold uppercase">Total POs</p>
              <p className="text-2xl font-bold text-white">{poHeaders.length}</p>
            </div>
          </div>
        </div>

        {/* ✅ Search + Filters always open/visible */}
        <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg p-6 mb-6 border border-white/20">
          <div className="flex flex-col gap-4">

            {/* Search */}
            <div className="flex gap-4 items-center">
              <Search className="w-5 h-5 text-gray-400 shrink-0" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                placeholder="Search by PO Number, Vendor Name, Location..."
                className="flex-1 px-4 py-3 border border-gray-200 rounded-lg bg-white/50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            {/* ✅ Filters always visible */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-2">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  <option value="all">All Status</option>
                  <option value="0">Draft</option>
                  <option value="1">Pending</option>
                  <option value="2">Approved</option>
                  <option value="3">Rejected</option>
                  <option value="4">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-2">Items Per Page</label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  <option value="5">5 per page</option>
                  <option value="10">10 per page</option>
                  <option value="25">25 per page</option>
                  <option value="50">50 per page</option>
                  <option value="100">100 per page</option>
                </select>
              </div>
            </div>

            {/* Active filter tag */}
            {statusFilter !== 'all' && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold text-gray-600">Active Filters:</span>
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                  Status: {['Draft', 'Pending', 'Approved', 'Rejected', 'Cancelled'][Number(statusFilter)]}
                  <button onClick={() => setStatusFilter('all')} className="ml-2 hover:text-blue-900">×</button>
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {[
            { label: 'Pending', status: 1, color: 'text-yellow-600' },
            { label: 'Approved', status: 2, color: 'text-green-600' },
            { label: 'Rejected', status: 3, color: 'text-red-600' },
            { label: 'Draft', status: 0, color: 'text-gray-600' },
            { label: 'Cancelled', status: 4, color: 'text-gray-600' },
          ].map(({ label, status, color }) => (
            <div
              key={status}
              onClick={() => { setStatusFilter(status.toString()); setCurrentPage(1); }}
              className="bg-white/80 backdrop-blur-md p-4 rounded-lg shadow border border-white/20 cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all"
            >
              <p className="text-xs text-gray-600 uppercase font-semibold">{label}</p>
              <p className={`text-2xl font-bold ${color}`}>
                {poHeaders.filter(po => po.status === status).length}
              </p>
            </div>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 overflow-hidden">
          <div className="px-6 py-3 bg-gray-50/50 border-b border-gray-200">
            <p className="text-sm text-gray-600">
              Showing <span className="font-semibold text-gray-900">{filteredPOs.length === 0 ? 0 : indexOfFirstItem + 1}</span> to{' '}
              <span className="font-semibold text-gray-900">{Math.min(indexOfLastItem, filteredPOs.length)}</span> of{' '}
              <span className="font-semibold text-gray-900">{filteredPOs.length}</span> results
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
              <p className="text-gray-500 mt-4 text-sm font-medium">Loading purchase orders...</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                    <tr>
                      {[
                        { label: 'PO Number', key: 'poNumber' },
                        { label: 'Status', key: 'status' },
                        { label: 'Vendor', key: 'vendorId' },
                        { label: 'Ship To', key: 'shipTo_Location' },
                        { label: 'Created Date', key: 'poCreatedDate' },
                        { label: 'Delivery Date', key: 'deliveryDate' },
                      ].map(({ label, key }) => (
                        <th
                          key={key}
                          className="px-6 py-4 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700 transition-colors"
                          onClick={() => handleSort(key)}
                        >
                          <div className="flex items-center gap-1">
                            {label}
                            {getSortIcon(key)}
                          </div>
                        </th>
                      ))}
                      <th className="px-6 py-4 text-center text-xs font-semibold uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentItems.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                          <p className="text-lg">No purchase orders found</p>
                        </td>
                      </tr>
                    ) : (
                      currentItems.map((po) => (
                        <tr key={po.id} className="hover:bg-blue-50/50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-blue-600" />
                              <span className="font-semibold text-gray-900">{po.poNumber}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">{getStatusBadge(po.status)}</td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <Building className="w-4 h-4 text-gray-400 shrink-0" />
                              <span className="text-sm text-gray-700 font-medium">
                                {vendors[po.vendorId] || `Vendor ${po.vendorId}`}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 font-medium max-w-xs truncate">{po.shipTo_Location}</div>
                            <div className="text-xs text-gray-500 truncate">{po.shipTo_Address1}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-700">{formatDate(po.poCreatedDate)}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <TrendingUp className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-700">{formatDate(po.deliveryDate)}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <button
                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-all shadow-md"
                              onClick={() => handleViewPO(po)}
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {filteredPOs.length > 0 && (
                <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      Page <span className="font-semibold text-gray-900">{currentPage}</span> of{' '}
                      <span className="font-semibold text-gray-900">{totalPages}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => paginate(1)} disabled={currentPage === 1}
                        className="p-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                        <ChevronsLeft className="w-4 h-4" />
                      </button>
                      <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}
                        className="p-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <div className="flex items-center gap-1">
                        {getPageNumbers().map((page, index) => (
                          page === '...' ? (
                            <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-500">...</span>
                          ) : (
                            <button key={page} onClick={() => paginate(page)}
                              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                                currentPage === page
                                  ? 'bg-blue-600 text-white shadow-md'
                                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}>
                              {page}
                            </button>
                          )
                        ))}
                      </div>
                      <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages}
                        className="p-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                        <ChevronRight className="w-4 h-4" />
                      </button>
                      <button onClick={() => paginate(totalPages)} disabled={currentPage === totalPages}
                        className="p-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                        <ChevronsRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {isModalOpen && selectedPO && (
        <POApprovalModal
          po={selectedPO}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onApprove={handleApprove}
          onReject={handleReject}
          getStatusBadge={getStatusBadge}
          formatDate={formatDate}
        />
      )}
    </div>
  );
};

export default POApproval;